import { Outlet, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, ArrowRight, ChevronRight, Shield } from "lucide-react";
import { gcPath, gcCanonicalBase } from "./gcDomain";
import { JsonLd } from "../../components/JsonLd";

const CTA = "https://immigrationguide.us";
const BASE = "https://immigrationguide.us";

function navLinks() {
  return [
    { to: gcPath("/"), label: "Home" },
    { to: gcPath("/green-card-guide"), label: "Green Card Guide" },
    { to: gcPath("/green-card-timeline"), label: "Timeline" },
    { to: gcPath("/documents-checklist"), label: "Documents" },
    { to: gcPath("/faq"), label: "FAQ" },
  ];
}

function GCNav() {
  const { pathname } = useLocation();
  const links = navLinks();
  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="gc-nav">
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to={gcPath("/")} className="flex items-center gap-2 font-heading font-bold text-lg text-slate-900" data-testid="gc-logo">
          <FileText className="h-5 w-5 text-emerald-600" />
          GreenCardGuide
        </Link>
        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                pathname === l.to
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>
        <a
          href={CTA}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          data-testid="gc-nav-cta"
        >
          Get Full Access <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </nav>
  );
}

function GCFooter() {
  const links = navLinks();
  return (
    <footer className="bg-slate-900 text-slate-400 py-12" data-testid="gc-footer">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-emerald-400" />
              <span className="font-heading font-bold text-white">GreenCardGuide</span>
            </div>
            <p className="text-sm leading-relaxed">
              Free educational resource for understanding the U.S. Green Card process. Not legal advice.
            </p>
          </div>
          <div>
            <h4 className="text-white font-medium text-sm mb-3">Resources</h4>
            <ul className="space-y-2 text-sm">
              {links.slice(1).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-medium text-sm mb-3">Full Platform</h4>
            <p className="text-sm mb-3">
              Need step-by-step AI guidance, form filling help, and deadline tracking?
            </p>
            <a
              href={CTA}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
            >
              Visit ImmigrationGuide.us <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs">&copy; {new Date().getFullYear()} ImmigrationGuide.us. All rights reserved.</p>
          <p className="text-xs">
            Contact: <a href="mailto:support@immigrationguide.us" className="hover:text-white">support@immigrationguide.us</a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export function GCSEOHead({ title, description, path }) {
  const base = gcCanonicalBase();
  const pageTitle = title ? `${title} | ImmigrationGuide` : "Green Card Guide | ImmigrationGuide";
  const canonical = path && path !== "/" ? `${base}${path}` : base;
  const ogImage = `${BASE}/og-image.png`;

  return (
    <Helmet>
      <html lang="en" />
      <title>{pageTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="ImmigrationGuide" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}

/** Breadcrumb navigation for inner GC pages */
export function Breadcrumbs({ current }) {
  const base = gcCanonicalBase();
  return (
    <>
      <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto px-4 pt-4" data-testid="gc-breadcrumbs">
        <ol className="flex items-center gap-1 text-sm text-slate-500">
          <li>
            <Link to={gcPath("/")} className="hover:text-emerald-700 transition-colors">
              Green Card Guide
            </Link>
          </li>
          <li><ChevronRight className="h-3.5 w-3.5" /></li>
          <li className="text-slate-800 font-medium">{current}</li>
        </ol>
      </nav>
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Green Card Guide", item: base },
              { "@type": "ListItem", position: 2, name: current },
            ],
          }} />
    </>
  );
}

const ALL_GUIDES = [
  { path: "/green-card-guide", label: "Complete Green Card Guide", desc: "All categories and pathways explained" },
  { path: "/green-card-timeline", label: "Processing Timeline", desc: "Realistic timelines for every category" },
  { path: "/documents-checklist", label: "Documents Checklist", desc: "Every required document organized by type" },
  { path: "/faq", label: "FAQ", desc: "19 answers to common Green Card questions" },
];

/** Related guides cross-linking section — excludes the current page */
export function RelatedGuides({ currentPath }) {
  const related = ALL_GUIDES.filter((g) => g.path !== currentPath);
  return (
    <section className="py-10 border-t border-slate-100" data-testid="gc-related-guides">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">Continue Reading</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {related.map((g) => (
            <Link
              key={g.path}
              to={gcPath(g.path)}
              className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all"
              data-testid={`gc-related-${g.path.slice(1)}`}
            >
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">
                {g.label}
              </h3>
              <p className="text-xs text-slate-500">{g.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTABanner() {
  return (
    <section className="bg-emerald-600 py-12 md:py-16" data-testid="gc-cta-banner">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-3">
          Ready to Start Your Green Card Journey?
        </h2>
        <p className="text-emerald-100 mb-6 text-base md:text-lg">
          Get AI-powered step-by-step guidance, form auto-fill, deadline tracking, and expert support.
        </p>
        <a
          href={CTA}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-8 py-3 rounded-lg text-base transition-colors shadow-lg"
          data-testid="gc-cta-main"
        >
          Start Free on ImmigrationGuide <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}

/** Trust badge with last-updated date */
export function LastUpdated({ date = "February 2026" }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="gc-last-updated">
      <Shield className="h-3.5 w-3.5 text-emerald-600" />
      <span>Last updated: {date} · Based on official USCIS data</span>
    </div>
  );
}

export default function GCLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <GCNav />
      <main className="flex-1">
        <Outlet />
      </main>
      <GCFooter />
    </div>
  );
}
